import {
    makeChatUrl,
    trans,
} from '@frontend/helpers'
import type {ChatDetailsData} from '@frontend/modules/private/chats/ChatsModuleTypes'
import {useUiLayoutStore} from '@frontend/stores'
import {
    mdiCheckAll,
    mdiCheckboxBlankOutline,
    mdiCheckboxMarked,
} from '@mdi/js'
import clsx from 'clsx'
import {type MouseEvent} from 'react'
import {Link} from 'react-router-dom'
import {CheckboxIconSwitch} from 'swayok-react-mdb-ui-kit/components/Icon/CheckboxIconSwitch'
import {Icon} from 'swayok-react-mdb-ui-kit/components/Icon/Icon'
import {getChatHeaderData} from '../heplers/getChatHeaderData'
import {ChatActionsMenu} from './ChatActionsMenu'
import {ChatCover} from './ChatCover'

interface Props {
    miniChat?: boolean
    chat: ChatDetailsData
    className?: string
    selected: boolean
    isLast?: boolean
    disabled?: boolean
    onSelect: (chatId: number) => void
    onChatOpen: (chat: ChatDetailsData) => void
    // Удаление чата.
    onDelete: (chat: ChatDetailsData) => void
    // Прочитать все сообщения в чате.
    onReadAllMessages: (chat: ChatDetailsData) => void
    // Показать окно добавления отзыва о продавце.
    onAddReview: (chat: ChatDetailsData) => void
}

// Отображение списка чатов.
export function ChatsListItem(props: Props) {

    const {
        miniChat,
        chat,
        className,
        selected,
        isLast,
        disabled,
        onSelect,
        onChatOpen,
        onDelete,
        onReadAllMessages,
        onAddReview,
    } = props

    // Отключить стандартную обработку события
    // и предотвратить передачу события родительскому элементу.
    // Если этого не сделать, то совершится переход к сообщениям чата.
    const stopDefaultEvent = (event: MouseEvent<HTMLElement>) => {
        event.preventDefault()
        event.stopPropagation()
    }

    const headerData = getChatHeaderData(chat)
    const lastActivityAt: string = chat.lastMessageCreatedAt ?? chat.createdAt
    const isMobile: boolean = useUiLayoutStore(
        state => state.deviceType === 'mobile'
    )

    const checkbox = !miniChat && (
        <CheckboxIconSwitch
            active={selected}
            disabled={disabled}
            activeIcon={mdiCheckboxMarked}
            inactiveIcon={mdiCheckboxBlankOutline}
            onClick={event => {
                stopDefaultEvent(event)
                onSelect?.(chat.id)
            }}
        />
    )

    const cover = (
        <ChatCover
            coverImage={headerData.coverImage}
            userName={chat.otherUser.name}
            large={!miniChat && !isMobile}
        />
    )

    const name = (
        <span className="flex-1 fs-6 fw-800 text-truncate">
            {headerData.sellerName}
        </span>
    )

    const date = (
        <span className="d-flex flex-row align-items-center gap-1 flex-shrink-0 text-muted fs-7 text-nowrap">
            {!chat.hasNotViewedMessages && (
                <Icon
                    path={mdiCheckAll}
                    size={16}
                    color="light-green"
                />
            )}
            {lastActivityAt}
        </span>
    )

    const menu = (
        <ChatActionsMenu
            chat={chat}
            miniChat={miniChat}
            isLast={isLast}
            disabled={disabled}
            // Кнопка выше строки с именем, отрицательный отступ не дает ей раздувать карточку.
            className="flex-shrink-0 my-n2"
            buttonClassName="p-1"
            onRead={onReadAllMessages}
            onDelete={onDelete}
            onAddReview={onAddReview}
        />
    )

    const title = (
        <span className="text-muted text-truncate mw-100">
            {headerData.title}
        </span>
    )

    const price = chat.ad && (
        <span className={clsx('fw-700 text-nowrap flex-shrink-0', !miniChat && 'fs-5')}>
            {chat.ad.price}
        </span>
    )

    const lastMessage = (
        <div className="text-truncate">
            {chat.lastMessage || trans().cabinet.chats.no_last_message}
        </div>
    )

    // Мини-чат - маленькое окно поверх сайта: плотная строка, чтобы чатов помещалось больше.
    const miniChatLayout = () => (
        <>
            {cover}
            <div className="flex-1 d-flex flex-column gap-1 overflow-hidden">
                <div className="d-flex flex-row align-items-center gap-2">
                    {name}
                    {date}
                    {menu}
                </div>
                <div className="d-flex flex-row align-items-center col-gap-2 overflow-hidden">
                    {title}
                    {price}
                </div>
                {lastMessage}
            </div>
        </>
    )

    const mobileLayout = () => (
        <>
            <div className="d-flex flex-row align-items-center gap-2">
                {checkbox}
                <span className="flex-1" />
                {date}
                {menu}
            </div>
            <div className="d-flex flex-row align-items-center gap-small">
                {cover}
                <div className="flex-1 d-flex flex-column gap-1 overflow-hidden">
                    {name}
                    {title}
                    {price}
                </div>
            </div>
            {lastMessage}
        </>
    )

    const desktopLayout = () => (
        <>
            {checkbox}
            {cover}
            <div className="flex-1 d-flex flex-column gap-3 overflow-hidden">
                <div className="d-flex flex-column gap-2">
                    <div className="d-flex flex-row align-items-start gap-2">
                        {name}
                        {date}
                        {menu}
                    </div>
                    <div className="d-flex flex-row align-items-center col-gap-3 overflow-hidden">
                        {title}
                        {price}
                    </div>
                </div>
                {lastMessage}
            </div>
        </>
    )

    return (
        <Link
            to={makeChatUrl(chat.id)}
            className={clsx(
                'chats-list-item d-flex mb-2 border rounded-9 lh-sm',
                miniChat && 'flex-row align-items-center gap-small p-2',
                !miniChat && isMobile && 'flex-column gap-2 p-3',
                !miniChat && !isMobile && 'flex-row align-items-center gap-3 p-3 p-sm-4',
                chat.hasNotViewedMessages ? 'bg-light-blue' : 'bg-white',
                className
            )}
            onClick={event => {
                event.preventDefault()
                onChatOpen(chat)
            }}
        >
            {miniChat ? miniChatLayout() : isMobile ? mobileLayout() : desktopLayout()}
        </Link>
    )
}
